<template>
  <div
    class="snackbar-item relative flex items-center justify-between gap-[12px] rounded-xl p-[16px] shadow-xl overflow-hidden w-[calc(100vw-32px)] md:w-auto md:min-w-[340px] md:max-w-[480px] bg-[#292B2D]"
    :role="snackbar.type === 'error' || snackbar.type === 'warning' ? 'alert' : 'status'"
    aria-atomic="true"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <div class="flex items-center gap-[8px] flex-1 min-w-0">
      <div
        class="flex-shrink-0 w-[36px] h-[36px] rounded-full flex items-center justify-center"
        :style="{ backgroundColor: config.iconBg }"
      >
        <BaseIcon :name="config.iconName" color="black" class="w-[22px] h-[22px]" />
      </div>

      <span class="flex-1 min-w-0 text-gray-00 label-2 break-words">
        {{ snackbar.message }}
      </span>
    </div>

    <div class="flex items-center gap-[8px] flex-shrink-0">
      <BaseButton
        v-if="snackbar.action"
        variant="primary"
        size="sm"
        @click="onActionClick"
      >
        {{ snackbar.action.label }}
      </BaseButton>

      <button
        v-if="snackbar.showClose"
        class="flex items-center justify-center w-[24px] h-[24px] rounded-md text-gray-200 hover:text-white hover:bg-white/10 transition-colors"
        @click="$emit('close')"
      >
        <BaseIcon name="close" color="currentColor" class="w-[16px] h-[16px]" />
      </button>
    </div>

    <div
      v-if="showProgressBar"
      class="progress-bar absolute bottom-0 left-0 right-0 h-[3px] rounded-sm"
      :style="progressStyle"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import BaseIcon from '@/components/foundations/BaseIcon.vue'
import BaseButton from '@/components/foundations/BaseButton.vue'

const props = defineProps({
  snackbar: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['close'])

const isPaused = ref(false)
let timer = null
let remaining = null
let startTime = null

function pause() {
  if (!remaining) return
  clearTimeout(timer)
  remaining -= Date.now() - startTime
  isPaused.value = true
}

function resume() {
  if (!remaining) return
  isPaused.value = false
  startTime = Date.now()
  timer = setTimeout(() => emit('close'), remaining)
}

onMounted(() => {
  if (props.snackbar.duration) {
    remaining = props.snackbar.duration
    resume()
  }
})
onUnmounted(() => clearTimeout(timer))

function onActionClick() {
  props.snackbar.action?.handler?.()
  emit('close')
}

const TYPE_CONFIG = {
  success: {
    iconName: 'check',
    iconBg: '#43C152',
    progressColor: '#43C152',
  },
  error: {
    iconName: 'close',
    iconBg: '#EB5457',
    progressColor: '#EB5457',
  },
  warning: {
    iconName: 'alert-triangle',
    iconBg: '#EFBD42',
    progressColor: '#EFBD42',
  },
  info: {
    iconName: 'information-slab-circle-outline',
    iconBg: '#95B8FF',
    progressColor: '#95B8FF',
  },
}

const config = computed(() => TYPE_CONFIG[props.snackbar.type] ?? TYPE_CONFIG.info)

const showProgressBar = computed(() => !!props.snackbar.duration && props.snackbar.showProgress)

const progressStyle = computed(() => ({
  animationDuration: `${props.snackbar.duration}ms`,
  animationPlayState: isPaused.value ? 'paused' : 'running',
  backgroundColor: config.value.progressColor,
}))
</script>

<style scoped>
@keyframes progress-shrink {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}

.progress-bar {
  transform-origin: left center;
  animation-name: progress-shrink;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}
</style>
